import { toMonthlyPrice } from '@brand-lolly/app/billing/plan-interval/plan-interval.util';
import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';
import {
	formatPlanPrice,
	ladderFooterNote,
	ladderStopLabel
} from './plan-tier-copy';

/**
 * One stop on a tier's allowance ladder, BROKEN INTO THE PARTS THE OPTION DRAWS
 * rather than folded into one string — which is the whole reason this file
 * exists.
 *
 * A LADDER IS A DIMENSION INSIDE A TIER, not a set of tiers. The application
 * product seeds one PLAN per (tier, rung, interval): Pro has five rungs and
 * Business nine, so the catalogue holds 29 shown rows for three tiers. The tier
 * is the card; the rung is the dial.
 */
export interface PlanCardLadderStop {
	/**
	 * The rung on ONE line — "400 credits — $164/mo".
	 *
	 * Still here, and still the thing a single-rung dial reads and the details
	 * dialog names, because both of those are one run of text with nowhere to
	 * put a pill. The open panel does not use it: it draws {@link quantity},
	 * {@link savingPercent} and {@link price} as three elements.
	 */
	label: string;
	/** The left half of an option, and what the closed trigger reads:
	 *  "400 credits". A rung stating no allowance falls back to its plan title. */
	quantity: string;
	/** The right half: "$164/mo". Empty on a rung that costs nothing, where
	 *  "— $0/mo" is a price nobody compares against anything. */
	price: string;
	/**
	 * The VOLUME saving this rung carries, in whole percent — the pill.
	 *
	 * `null`, and therefore no pill at all, whenever it rounds to zero: the
	 * opening rungs of a tier are priced at a flat rate by design, so a "Save
	 * 0%" pill would be worse than none. See {@link buildPlanLadder} for what
	 * the figure is measured against.
	 */
	savingPercent: number | null;
}

/** A tier's whole ladder as the card needs it — the rungs, and the line under
 *  them that says where to go when the top rung is not enough. */
export interface PlanCardLadder {
	stops: PlanCardLadderStop[];
	/** The upsell under the rungs ("More than 1,200? Try Business"). Empty
	 *  where the tier has nowhere to send the reader. */
	footerNote: string;
}

/** A rung's price per credit, per month. `null` where either half is missing or
 *  zero — a free rung has no rate, and a rung with no stated allowance has no
 *  denominator. */
function perCreditRate(
	plan: CatalogPlan,
	credits: number | null
): number | null {
	if (credits === null || credits <= 0) {
		return null;
	}
	const monthly = parseFloat(toMonthlyPrice(plan));
	if (isNaN(monthly) || monthly <= 0) {
		return null;
	}
	return monthly / credits;
}

/**
 * THE TIER'S ALLOWANCE LADDER, WITH THE SAVING EACH RUNG ACTUALLY CARRIES.
 *
 * WHERE THE SAVING COMES FROM, because the page must not claim a discount the
 * catalogue does not give. It is DERIVED from two fields the catalogue already
 * returns on the very plans this ladder is built from — `price` (with
 * `interval`, so a yearly rung is compared at its per-month twelfth) and the
 * `app_credits_grant_per_month` entitlement cap that {@link creditsOf} reads.
 * Nothing is fetched, nothing is assumed, and in particular no per-credit rate
 * is read off the plan: `plans.list_usd_per_credit` DOES NOT EXIST, and
 * publishing one would be a schema change. Two prices the API already returns,
 * divided by two allowances it already returns, is not.
 *
 * The comparison is the rung's per-credit rate against THE TIER'S OWN BASE RATE
 * — the rate on its cheapest rung — so a card only ever measures itself against
 * itself. Pro opens flat ($0.33/credit at both 100 and 200) and then steps down,
 * which is why its first two rungs carry no pill and that is correct rather
 * than a bug.
 *
 * Rounded with `Math.round`, and a saving that rounds to zero produces `null`
 * rather than a "Save 0%" pill.
 *
 * The figure is INTERVAL-INDEPENDENT by construction: the yearly ladder is a
 * uniform ten-twelfths of the monthly one, so the ratio between two rungs is
 * identical either way and the pill does not flicker when the billing toggle
 * moves. It is measured per interval anyway rather than assumed, because that
 * uniformity is a property of today's seed and not a promise.
 *
 * @param rungs      the tier's plans, ALREADY SORTED BY ALLOWANCE — the caller
 *                   sorts them because it also picks the selected index out of
 *                   the same order.
 * @param creditsOf  the caller's own allowance reader, so the two pages that
 *                   show this card cannot disagree about which entitlement the
 *                   allowance is.
 */
export function buildPlanLadder(
	rungs: CatalogPlan[],
	creditsOf: (plan: CatalogPlan) => number | null
): PlanCardLadder {
	// The cheapest rung that HAS a rate — not simply the first, since a tier
	// whose opening rung is free (the free tier) states no rate at all and would
	// otherwise leave every rung above it measured against nothing.
	let baseRate: number | null = null;
	for (const rung of rungs) {
		const rate = perCreditRate(rung, creditsOf(rung));
		if (rate !== null) {
			baseRate = rate;
			break;
		}
	}

	const stops = rungs.map(rung => {
		const credits = creditsOf(rung);
		const rate = perCreditRate(rung, credits);
		const saving =
			rate !== null && baseRate !== null && baseRate > 0
				? Math.round((1 - rate / baseRate) * 100)
				: 0;
		return {
			label: ladderStopLabel(rung, credits),
			quantity:
				credits === null
					? rung.title
					: `${credits.toLocaleString('en-US')} credits`,
			price:
				rate === null
					? ''
					: `${formatPlanPrice(toMonthlyPrice(rung))}/mo`,
			savingPercent: saving > 0 ? saving : null
		};
	});

	return { stops, footerNote: ladderFooterNote(rungs, creditsOf) };
}
